<!-- ─── Trigger row ──────────────────────────────────────────────────────── -->
<div class="flex items-center gap-1 min-w-0">

  <!-- Active-list dropdown trigger -->
  <div class="relative">
    <button
      type="button"
      class="btn btn-sm btn-ghost gap-1.5 min-w-0 max-w-[140px] sm:max-w-xs font-semibold"
      (click)="toggleDropdown()"
      [attr.aria-expanded]="dropdownOpen"
      aria-haspopup="listbox">
      <span class="truncate">{{ activeList?.name ?? 'Shopping List' }}</span>
      <fa-icon [icon]="faChevronDown"
               class="text-xs shrink-0 transition-transform duration-200"
               [class.rotate-180]="dropdownOpen"></fa-icon>
    </button>

    <!-- ── Dropdown panel ── -->
    @if (dropdownOpen) {
      <!-- Backdrop (invisible) to catch outside clicks -->
      <div
        class="fixed inset-0 z-30"
        (click)="closeDropdown()">
      </div>

      <div
        class="absolute left-0 top-full mt-1 z-40 min-w-[220px] w-max max-w-xs
               bg-base-100 border border-base-300 rounded-xl shadow-lg
               overflow-hidden flex flex-col">

        <!-- Create new list -->
        <div class="p-2 border-b border-base-200">
          <button
            type="button"
            class="btn btn-sm btn-ghost w-full justify-start gap-2 text-primary font-medium"
            (click)="openCreate()">
            <fa-icon [icon]="faPlus" class="text-xs"></fa-icon>
            New list
          </button>
        </div>

        <!-- List of existing lists -->
        <ul class="py-1 overflow-y-auto max-h-72" role="listbox">
          @for (list of lists; track list.id) {
            <li class="group flex items-center gap-1 px-1 hover:bg-base-200 transition-colors rounded-lg mx-1">
              <!-- Row main button -->
              <button
                type="button"
                class="flex-1 flex items-center gap-2 px-2 py-2 text-sm text-left min-w-0"
                role="option"
                [attr.aria-selected]="list.id === activeListId"
                (click)="selectList(list)">
                <!-- active checkmark -->
                <span class="w-4 shrink-0 flex items-center justify-center">
                  @if (list.id === activeListId) {
                    <fa-icon [icon]="faCheck" class="text-xs text-primary"></fa-icon>
                  }
                </span>
                <span class="truncate font-medium" [class.text-primary]="list.id === activeListId">
                  {{ list.name }}
                </span>
                @if (list.is_active) {
                  <span class="badge badge-xs badge-success ml-auto shrink-0">active</span>
                }
              </button>

              <!-- no kebab here anymore -->
            </li>
          }
        </ul>
      </div>
    }
  </div>

  <!-- Active-list overflow menu (rename) -->
  <div class="relative">
    <button
      type="button"
      class="btn btn-sm btn-ghost w-8 h-8 p-0"
      aria-label="List options"
      (click)="toggleOverflowMenu($event)">
      <fa-icon [icon]="faBars" class="text-sm"></fa-icon>
    </button>

    @if (overflowMenuOpen) {
      <div class="fixed inset-0 z-30" (click)="overflowMenuOpen = false"></div>
      <div class="absolute left-0 sm:left-auto sm:right-0 top-full mt-1 z-40 w-44
                  bg-base-100 border border-base-300 rounded-xl shadow-lg
                  overflow-hidden py-1">
        <button
          type="button"
          class="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-base-200 transition-colors"
          (click)="openRenameActive($event)">
          <fa-icon [icon]="faPencil" class="text-xs text-base-content/50"></fa-icon>
          Rename list
        </button>
        @if (activeList && canDelete()) {
          <div class="border-t border-base-200 my-1"></div>
          <button
            type="button"
            class="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-error/10 text-error transition-colors"
            (click)="openDeleteActive($event)">
            <fa-icon [icon]="faTrash" class="text-xs"></fa-icon>
            Delete list
          </button>
        }
      </div>
    }
  </div>

</div>

<!-- ─── Create list modal ──────────────────────────────────────────────── -->
<ui-modal #createModal title="New shopping list" size="sm">
  <div class="space-y-4">
    <div>
      <label class="label label-text text-sm font-medium" for="new-list-name-input">List name</label>
      <input
        id="new-list-name-input"
        type="text"
        class="input input-bordered w-full"
        [(ngModel)]="newListName"
        placeholder="e.g. Weekly groceries"
        (keydown.enter)="submitCreate()"
        (keydown.escape)="createModal.close()"
        maxlength="80" />
    </div>
    <div class="flex justify-end gap-2 pt-1">
      <button type="button" class="btn btn-ghost btn-sm" (click)="createModal.close()">Cancel</button>
      <button
        type="button"
        class="btn btn-primary btn-sm"
        [disabled]="!newListName.trim() || creating"
        (click)="submitCreate()">
        @if (creating) { <span class="loading loading-spinner loading-xs"></span> }
        Create &amp; switch
      </button>
    </div>
  </div>
</ui-modal>

<!-- ─── Rename modal ───────────────────────────────────────────────────── -->
<ui-modal #renameModal title="Rename list" size="sm">
  <div class="space-y-4">
    <input
      id="rename-list-input"
      type="text"
      class="input input-bordered w-full"
      [(ngModel)]="renameValue"
      placeholder="List name"
      (keydown.enter)="submitRename()"
      (keydown.escape)="renameModal.close()"
      maxlength="80" />
    <div class="flex justify-end gap-2 pt-1">
      <button type="button" class="btn btn-ghost btn-sm" (click)="renameModal.close()">Cancel</button>
      <button
        type="button"
        class="btn btn-primary btn-sm"
        [disabled]="!renameValue.trim() || renaming"
        (click)="submitRename()">
        @if (renaming) { <span class="loading loading-spinner loading-xs"></span> }
        Save
      </button>
    </div>
  </div>
</ui-modal>

<!-- ─── Delete confirmation modal ─────────────────────────────────────── -->
<ui-modal #deleteModal title="Delete list?" size="sm">
  <div class="space-y-4">
    <p class="text-sm text-base-content/80">
      This will permanently remove the shopping list
      <strong>"{{ deletingList?.name }}"</strong>
      and all its items. This cannot be undone.
    </p>
    <div class="flex justify-end gap-2 pt-1">
      <button type="button" class="btn btn-ghost btn-sm" (click)="deleteModal.close()">Cancel</button>
      <button
        type="button"
        class="btn btn-error btn-sm"
        [disabled]="deleting"
        (click)="submitDelete()">
        @if (deleting) { <span class="loading loading-spinner loading-xs"></span> }
        Delete
      </button>
    </div>
  </div>
</ui-modal>





